/**
 * WCAG 2.1 A fix: "ARIA hidden element must not be focusable or contain
 * focusable elements".
 *
 * The theme's carousel library (Flickity, bundled in global.min.js) marks
 * off-screen carousel slides with aria-hidden="true" but never removes
 * those slides' links/buttons from the tab order, so keyboard and screen
 * reader users can still tab into content that is announced as hidden.
 *
 * Rather than patching the bundled/minified library, this script watches
 * the whole page for aria-hidden being toggled (on carousel slides or
 * anywhere else) and keeps each hidden element's focusable descendants in
 * sync by toggling tabindex="-1" on them. When the element becomes visible
 * again, the original tabindex (or none) is restored.
 *
 * It also watches for brand-new elements being inserted into the page
 * (e.g. when the theme re-renders a product card's Add to Cart button
 * after a currency update, or fetches/rebuilds a whole block such as the
 * cart drawer's "you may also like" carousel) and immediately disables
 * their tab order if they landed inside a container that is already
 * aria-hidden="true".
 *
 * As a safety net on top of the MutationObserver (which should catch
 * everything, but carousel libraries can init/reflow in ways that are
 * hard to fully anticipate — e.g. sections rebuilt via innerHTML from an
 * async fetch, or attribute writes that briefly race with our listener
 * being attached), a lightweight periodic re-scan of the whole document
 * also runs every 500ms. This guarantees any missed case self-corrects
 * well within the time it takes a keyboard user to tab to it.
 */
(function () {
  "use strict";

  var FOCUSABLE_SELECTOR =
    'a[href], button, input, select, textarea, [tabindex]';
  var ADDED_FLAG = "data-a11y-tabindex-added";
  var RESCAN_INTERVAL_MS = 500;

  function collectFocusable(el) {
    var list = [];
    if (el.nodeType !== 1) return list;
    if (el.matches && el.matches(FOCUSABLE_SELECTOR)) list.push(el);
    var descendants = el.querySelectorAll(FOCUSABLE_SELECTOR);
    for (var i = 0; i < descendants.length; i++) list.push(descendants[i]);
    return list;
  }

  function disable(el) {
    if (!el.hasAttribute(ADDED_FLAG)) {
      el.setAttribute(ADDED_FLAG, el.getAttribute("tabindex") || "");
    }
    if (el.getAttribute("tabindex") !== "-1") {
      el.setAttribute("tabindex", "-1");
    }
  }

  function restore(el) {
    var previous = el.getAttribute(ADDED_FLAG);
    if (previous) {
      el.setAttribute("tabindex", previous);
    } else {
      el.removeAttribute("tabindex");
    }
    el.removeAttribute(ADDED_FLAG);
  }

  function syncNode(node) {
    if (!node || node.nodeType !== 1) return;
    var isHidden = node.getAttribute("aria-hidden") === "true";
    var focusables = collectFocusable(node);

    for (var i = 0; i < focusables.length; i++) {
      var el = focusables[i];
      if (isHidden) {
        disable(el);
      } else if (el.hasAttribute(ADDED_FLAG)) {
        restore(el);
      }
    }
  }

  function isWithinAriaHidden(el) {
    var node = el.parentElement;
    while (node) {
      if (node.getAttribute("aria-hidden") === "true") return true;
      node = node.parentElement;
    }
    return false;
  }

  function scanAll(root) {
    root = root || document;
    var hiddenEls = root.querySelectorAll('[aria-hidden="true"]');
    for (var i = 0; i < hiddenEls.length; i++) {
      syncNode(hiddenEls[i]);
    }
    var flagged = root.querySelectorAll("[" + ADDED_FLAG + "]");
    for (var j = 0; j < flagged.length; j++) {
      if (!isWithinAriaHidden(flagged[j]) && flagged[j].getAttribute("aria-hidden") !== "true") {
        restore(flagged[j]);
      }
    }
  }

  function handleAddedNode(node) {
    if (!node || node.nodeType !== 1) return;
    if (node.getAttribute("aria-hidden") === "true") {
      syncNode(node);
      return;
    }
    if (isWithinAriaHidden(node)) {
      var focusables = collectFocusable(node);
      for (var i = 0; i < focusables.length; i++) {
        disable(focusables[i]);
      }
    }
  }

  function start() {
    scanAll();

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];

        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "aria-hidden"
        ) {
          syncNode(mutation.target);
        } else if (mutation.type === "childList") {
          for (var j = 0; j < mutation.addedNodes.length; j++) {
            var added = mutation.addedNodes[j];
            handleAddedNode(added);
            if (added.nodeType === 1) {
              var nested = added.querySelectorAll
                ? added.querySelectorAll("*")
                : [];
              for (var k = 0; k < nested.length; k++) {
                handleAddedNode(nested[k]);
              }
            }
          }
        }
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["aria-hidden"],
      childList: true,
      subtree: true,
    });

    setInterval(scanAll, RESCAN_INTERVAL_MS);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
